import { useEffect, useState } from 'react';
import { Check, X, Trash2 } from 'lucide-react';
import { api, buildQuery } from '../../lib/api';
import type { Expense } from '../../types';
import { formatPrice, formatDate } from '../../lib/format';
import { Spinner, EmptyState, StatusBadge, ConfirmDialog } from '../../components/ui';

export default function Expenses() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get<Expense[]>(`/expenses${buildQuery({ status: statusFilter })}`)
      .then(setExpenses)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [statusFilter]);

  const setStatus = async (id: number, status: string) => {
    try {
      await api.patch(`/expenses/${id}/status`, { status });
      load();
    } catch {
      // ignore
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/expenses/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Expense Review</h1>

      <div className="mb-4">
        <select
          className="w-48 px-4 py-2 border border-gray-300 rounded-lg text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <Spinner label="Loading expenses..." />
      ) : expenses.length === 0 ? (
        <EmptyState message="No expenses found." />
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Submitted By</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Receipt</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => (
                <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500">{formatDate(e.expense_date)}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{e.user_name}</p>
                    <p className="text-xs text-gray-500 capitalize">{e.user_role}</p>
                  </td>
                  <td className="px-4 py-3">{e.category}</td>
                  <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{e.description || '—'}</td>
                  <td className="px-4 py-3 font-medium">{formatPrice(e.amount, e.currency)}</td>
                  <td className="px-4 py-3">
                    {e.receipt ? (
                      <a href={e.receipt} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs">View</a>
                    ) : (
                      <span className="text-gray-400 text-xs">None</span>
                    )}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-1">
                      {e.status === 'pending' && (
                        <>
                          <button
                            onClick={() => setStatus(e.id, 'approved')}
                            className="p-1.5 rounded-lg hover:bg-green-50 text-green-600"
                            title="Approve"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setStatus(e.id, 'rejected')}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
                            title="Reject"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setDeleteId(e.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDelete(deleteId)}
        title="Delete Expense"
        message="Are you sure you want to delete this expense?"
      />
    </div>
  );
}
